import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import AccountantTaxAuthorityValidityCell from './AccountantTaxAuthorityValidityCell';
import TaxAuthorityService from '../../../Services/taxAuthority.service';
import styles from './AccountantTaxAuthoritySection.module.scss';

const AccountantTaxAuthoritySection = ({
    accountant,
    token,
    handleTaxAuthorityAuth,
    setLoading,
    refreshNonce = 0,
}) => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'he' || i18n.language === 'ar';

    const [vatReportTokenDays, setVatReportTokenDays] = useState(0);
    const [vatReportTokenSeconds, setVatReportTokenSeconds] = useState(0);
    const [advancesPaymentTokenDays, setAdvancesPaymentTokenDays] = useState(0);
    const [advancesPaymentTokenSeconds, setAdvancesPaymentTokenSeconds] = useState(0);
    const [confirmationInfoTokenDays, setConfirmationInfoTokenDays] = useState(0);
    const [confirmationInfoTokenSeconds, setConfirmationInfoTokenSeconds] = useState(0);
    const [annex1214BTokenDays, setAnnex1214BTokenDays] = useState(0);
    const [annex1214BTokenSeconds, setAnnex1214BTokenSeconds] = useState(0);

    // Must match OAuth token Scope values (confirmation_information_scope → invoices_scope).
    const getBackendScope = (frontendScope) => {
        switch (frontendScope) {
            case 'VatReport':
                return 'vat_report_scope';
            case 'AdvancesPayment':
                return 'advances_payment_scope';
            case 'ConfirmationInformation':
                return 'invoices_scope';
            case 'Annex1214B':
                return 'annexes_scope';
            default:
                return frontendScope;
        }
    };

    const refreshTokenValidity = async (scope) => {
        if (!accountant?.accountantId || !token) return;

        try {
            let res = await TaxAuthorityService.getAccountantTokenDaysRemaining(
                accountant.accountantId,
                getBackendScope(scope),
                token
            );

            // Fallback for legacy ConfirmationInformation tokens stored as "scope"
            const hasTime = (res?.daysRemaining ?? 0) > 0 || (res?.secondsRemaining ?? 0) > 0;
            if (scope === 'ConfirmationInformation' && !hasTime) {
                res = await TaxAuthorityService.getAccountantTokenDaysRemaining(
                    accountant.accountantId,
                    'scope',
                    token
                );
            }

            switch (scope) {
                case 'VatReport':
                    setVatReportTokenDays(res?.daysRemaining ?? 0);
                    setVatReportTokenSeconds(res?.secondsRemaining ?? 0);
                    break;
                case 'AdvancesPayment':
                    setAdvancesPaymentTokenDays(res?.daysRemaining ?? 0);
                    setAdvancesPaymentTokenSeconds(res?.secondsRemaining ?? 0);
                    break;
                case 'ConfirmationInformation':
                    setConfirmationInfoTokenDays(res?.daysRemaining ?? 0);
                    setConfirmationInfoTokenSeconds(res?.secondsRemaining ?? 0);
                    break;
                case 'Annex1214B':
                    setAnnex1214BTokenDays(res?.daysRemaining ?? 0);
                    setAnnex1214BTokenSeconds(res?.secondsRemaining ?? 0);
                    break;
                default:
                    break;
            }
        } catch (e) {
            console.error(`Failed to refresh ${scope} token validity`, e);
            switch (scope) {
                case 'VatReport':
                    setVatReportTokenDays(0);
                    setVatReportTokenSeconds(0);
                    break;
                case 'AdvancesPayment':
                    setAdvancesPaymentTokenDays(0);
                    setAdvancesPaymentTokenSeconds(0);
                    break;
                case 'ConfirmationInformation':
                    setConfirmationInfoTokenDays(0);
                    setConfirmationInfoTokenSeconds(0);
                    break;
                case 'Annex1214B':
                    setAnnex1214BTokenDays(0);
                    setAnnex1214BTokenSeconds(0);
                    break;
                default:
                    break;
            }
        }
    };

    const refreshAllTokenValidities = async () => {
        await Promise.all([
            refreshTokenValidity('VatReport'),
            refreshTokenValidity('AdvancesPayment'),
            refreshTokenValidity('ConfirmationInformation'),
            refreshTokenValidity('Annex1214B'),
        ]);
    };

    useEffect(() => {
        if (accountant?.accountantId && token) {
            refreshAllTokenValidities();
            const interval = setInterval(refreshAllTokenValidities, 5 * 60 * 1000);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [accountant?.accountantId, token, refreshNonce]);

    if (!accountant?.accountantId) {
        return null;
    }

    const scopes = [
        { scope: 'VatReport', days: vatReportTokenDays, seconds: vatReportTokenSeconds },
        { scope: 'AdvancesPayment', days: advancesPaymentTokenDays, seconds: advancesPaymentTokenSeconds },
        { scope: 'ConfirmationInformation', days: confirmationInfoTokenDays, seconds: confirmationInfoTokenSeconds },
        { scope: 'Annex1214B', days: annex1214BTokenDays, seconds: annex1214BTokenSeconds },
    ];

    return (
        <div className={styles.section} dir={isRtl ? 'rtl' : 'ltr'}>
            <div className={styles.header}>
                <span className={styles.headerIcon}>
                    <CheckIcon />
                </span>
                <h3 className={styles.headerTitle}>{t('TaxAuthorityLoginValidity')}</h3>
            </div>

            <div className={styles.grid}>
                {scopes.map(({ scope, days, seconds }) => (
                    <AccountantTaxAuthorityValidityCell
                        key={scope}
                        scope={scope}
                        tokenDaysRemaining={days}
                        tokenSecondsRemaining={seconds}
                        t={t}
                        accountant={accountant}
                        TaxAuthorityService={TaxAuthorityService}
                        token={token}
                        handleTaxAuthorityAuth={handleTaxAuthorityAuth}
                        setLoading={setLoading}
                        refreshTokenValidity={refreshTokenValidity}
                    />
                ))}
            </div>
        </div>
    );
};

AccountantTaxAuthoritySection.propTypes = {
    accountant: PropTypes.object.isRequired,
    token: PropTypes.string,
    handleTaxAuthorityAuth: PropTypes.func.isRequired,
    setLoading: PropTypes.func.isRequired,
    refreshNonce: PropTypes.number,
};

export default AccountantTaxAuthoritySection;
